import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Calendar, Smartphone, Check, X } from "lucide-react";
import { api } from "@/lib/api";

const TIER_LABELS = { basic: "Basic", standard: "Standard", premium: "Premium" };

export default function ProtectionPlan() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/protection/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const included = [
    `${settings?.repairs_per_year || 1} Reparatur pro Vertragsjahr – Display ODER Akku, nach Wahl`,
    "Keine Zusatzkosten für die Reparatur selbst – nur der Versand (6,95 €) fällt an",
    "Original-OLED-Qualität bei Display-Reparaturen",
  ];
  const excluded = [
    "Vorschäden, die schon vor Vertragsbeginn bestanden",
    "Wasserschäden",
    "Schäden durch Fremdverschulden",
    "Andere Services wie Kamera oder Ladebuchse (regulär kostenpflichtig)",
  ];

  return (
    <section data-testid="protection-plan" className="relative z-10 bg-[#0a0a0a] py-24 lg:py-36 border-t border-white/10">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#888] mb-4">G-Repair Schutzpaket</p>
        <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-6 max-w-3xl">
          Einmal zahlen. Jedes Jahr abgesichert.
        </h1>
        <p className="text-[#aaa] text-base lg:text-lg max-w-2xl leading-relaxed mb-14">
          Mit dem Schutzpaket bekommst du jedes Jahr eine Reparatur für dein iPhone – Display oder Akku –
          ohne weitere Kosten außer dem Versand. Ein normaler Servicevertrag, keine Versicherung.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="lg:col-span-2 rounded-3xl border border-white/15 bg-[#050505] p-8 lg:p-10">
            <ShieldCheck size={32} className="text-white mb-6" />
            <p className="font-mono text-xs uppercase tracking-widest text-[#888] mb-3">Preisstufen</p>
            {settings ? (
              <div data-testid="protection-tier-prices" className="space-y-2 mb-6">
                {Object.keys(TIER_LABELS).map((tier) => (
                  <div key={tier} className="flex items-baseline justify-between rounded-xl bg-white/5 px-4 py-3">
                    <span className="text-sm text-[#ccc]">{TIER_LABELS[tier]}</span>
                    <span className="font-display text-xl text-white">{(settings.tiers[tier] ?? 0).toFixed(2)} <span className="text-xs text-[#888]">€/Monat</span></span>
                  </div>
                ))}
              </div>
            ) : (
              <div data-testid="protection-monthly-price-loading" className="h-[140px] w-full rounded-lg bg-white/5 animate-pulse mb-6" />
            )}
            <p className="text-xs text-[#666] mb-6">Dein Modell bestimmt automatisch die passende Stufe – der genaue Preis wird beim Abschluss angezeigt.</p>
            <div className="flex flex-wrap gap-2 mb-8">
              <span data-testid="protection-badge-term" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white/10 text-[#ddd] border border-white/10">
                <Calendar size={12} /> {settings?.min_term_months ? Math.round(settings.min_term_months / 12) : 2} Jahre Mindestlaufzeit
              </span>
              <span data-testid="protection-badge-quota" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white/10 text-[#ddd] border border-white/10">
                <Smartphone size={12} /> {settings?.repairs_per_year || 1} Reparatur/Jahr inklusive
              </span>
            </div>
            <button onClick={() => navigate("/schutzpaket/abschliessen")} data-testid="protection-signup-cta"
              className="w-full inline-flex items-center justify-center rounded-full bg-white text-[#050505] py-4 font-medium hover:bg-[#d1d5db] transition-colors">
              Schutzpaket abschließen
            </button>
            <p className="text-xs text-[#666] mt-4 text-center">Nach Ablauf der Mindestlaufzeit monatlich kündbar.</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-white/10 bg-[#050505] p-6">
              <p className="text-sm font-medium text-white mb-4">Enthalten</p>
              <ul className="space-y-3">
                {included.map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-[#bbb]">
                    <Check size={16} className="text-emerald-400 shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#050505] p-6">
              <p className="text-sm font-medium text-white mb-4">Nicht enthalten</p>
              <ul className="space-y-3">
                {excluded.map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-[#888]">
                    <X size={16} className="text-red-400/70 shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="sm:col-span-2 rounded-2xl border border-white/10 bg-[#050505] p-6 text-sm text-[#888] leading-relaxed">
              Nicht genutztes Kontingent verfällt zum Ende des Vertragsjahres und wird nicht übertragen.
              Versandkosten fallen bei jeder Reparatur normal an.
            </div>
          </motion.div>
        </div>

        <div className="mt-14 flex flex-wrap gap-6 text-sm">
          <button onClick={() => navigate("/schutzpaket/reparatur")} data-testid="protection-link-repair"
            className="text-[#ccc] hover:text-white underline underline-offset-4 transition-colors">
            Schon Kunde? Reparatur über dein Schutzpaket beauftragen
          </button>
          <button onClick={() => navigate("/schutzpaket/meins")} data-testid="protection-link-mine"
            className="text-[#ccc] hover:text-white underline underline-offset-4 transition-colors">
            Mein Schutzpaket verwalten
          </button>
        </div>
      </div>
    </section>
  );
}
